/**
 * The Owner's settings for the table — `PATCH …/config` is `requireOwner`.
 *
 * These were write-once: `DEFAULT_CAMPAIGN_CONFIG` carries no persona and no
 * tone, so every campaign started through the UI got a DM with neither and no
 * way to give it one. The settings existed, were read on every prompt, and
 * could only ever be changed by hand in the database.
 *
 * **HTTP, not a socket command.** The panels beside it nudge a live scene;
 * this edits durable configuration, and a form that answered "saved" or
 * "refused" through a fire-and-forget event would have no way to say which. It
 * also carries no copy of the defaults — the config it renders is the one the
 * server returned.
 *
 * **Every control here is acted on.** For a phase these three were stored and
 * read by nothing, and each carried a label saying so — a switch that silently
 * does nothing is worse than no switch, because it gets set and then planned
 * around. The labels are gone because the behaviour landed, and the hint
 * under each control now describes what it actually does: what happens to an
 * absent player's declaration, when an unprompted beat fires, and which verbs
 * theft gates.
 */

import { X_CARD_BOUNDARY_HELP } from './xCard.js';
import { AWAY_POLICIES, AWAY_POLICY_HELP, AWAY_POLICY_LABELS } from './awayPolicy.js';
import { useEffect, useState } from 'react';

import { ApiError, api } from '../api.js';

/**
 * What the server sends a member. `standingBoundaryCount` replaced the list itself, which comes
 * back absent for everyone else, so it is optional here for the same reason.
 */
interface CampaignConfig {
  gmPersona?: string;
  gmTone?: string;
  absentPlayerDefault: 'benched' | 'autoPass';
  ambientNarration: boolean;
  pvpTheft: boolean;
  /**
   * ⚠ **How many, never which.** The entries themselves reach no client.
   */
  standingBoundaryCount?: number;
}


export function SettingsPanel({ campaignId }: { readonly campaignId: string }) {
  const [config, setConfig] = useState<CampaignConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ campaign: { config: CampaignConfig } }>(`/api/campaigns/${campaignId}`)
      .then((body) => setConfig(body.campaign.config))
      .catch(() => setNote('could not load the settings'));
  }, [campaignId]);

  /**
   * Sends **only what changed**, and adopts the server's answer as the new
   * state rather than the value that was sent. A partial patch is the whole
   * point: the veto writes to this same blob, so a form that saved every field
   * would put a stale boundary list back over one the card had just added.
   */
  // `liftLast` is an instruction rather than a setting — it names no value,
  // because the thing it removes is one this client is not allowed to see.
  const save = (patch: Partial<CampaignConfig> | { liftLast: true }) => {
    setBusy(true);
    setNote(null);
    void api
      .patch<{ config: CampaignConfig }>(`/api/campaigns/${campaignId}/config`, patch)
      .then((body) => {
        setConfig(body.config);
        setNote('Saved.');
      })
      .catch((e: unknown) =>
        // The server's own words: it knows which rule was broken, and a
        // paraphrase here would be a second, worse copy of the rules.
        setNote(e instanceof ApiError ? e.message : 'the change was refused'),
      )
      .finally(() => setBusy(false));
  };

  if (!config) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">{note ?? 'Loading…'}</p>;
  }

  const boundaryCount = config.standingBoundaryCount ?? 0;

  return (
    <div className="space-y-4 text-sm">
      {note && <p className="text-xs text-neutral-500 dark:text-neutral-400">{note}</p>}

      <PersonaField
        id="gm-persona"
        label="Who the DM is"
        hint="Rides on every prompt, so keep it to a few lines."
        rows={3}
        value={config.gmPersona ?? ''}
        disabled={busy}
        onSave={(value) => save({ gmPersona: value })}
      />
      <PersonaField
        id="gm-tone"
        label="How the DM writes"
        hint="Register and pacing — “dry and sparing”, “lush and slow”."
        rows={2}
        value={config.gmTone ?? ''}
        disabled={busy}
        onSave={(value) => save({ gmTone: value })}
      />

      <div className="space-y-1">
        <label className="block text-xs text-neutral-500 dark:text-neutral-400" htmlFor="absent-default">
          When a player is away
        </label>
        <select
          id="absent-default"
          value={config.absentPlayerDefault}
          disabled={busy}
          onChange={(e) =>
            save({
              absentPlayerDefault: e.target.value as CampaignConfig['absentPlayerDefault'],
            })
          }
          className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
        >
          {AWAY_POLICIES.map(
            (policy) => (
              <option key={policy} value={policy}>
                {AWAY_POLICY_LABELS[policy]}
              </option>
            ),
          )}
        </select>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          The default for the table; a seat can override it in the roster on the Table tab. {AWAY_POLICY_HELP}
        </p>
      </div>

      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={config.ambientNarration}
          disabled={busy}
          onChange={(e) => save({ ambientNarration: e.target.checked })}
          className="mt-1"
        />
        <span>
          Unprompted narration
          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
            After five quiet minutes out of combat, the DM describes one small piece of the
            scene. Off by default — it interrupts roleplay and spends the model on beats nobody
            asked for. It never introduces anything: the engine stays the record of what
            happens.
          </span>
        </span>
      </label>

      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={config.pvpTheft}
          disabled={busy}
          onChange={(e) => save({ pvpTheft: e.target.checked })}
          className="mt-1"
        />
        <span>
          Theft between characters
          {/* Stated on the control, because the table is told when it
              changes and that is worth knowing before flipping it. */}
          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
            Under per-character visibility one player robbing another is trivial and genuinely
            invisible, so this is a decision the group makes in the open — switching it is
            announced to everyone. While it is off, a pick-pocket or take aimed at another
            character at this table is refused.
          </span>
        </span>
      </label>

      <div className="space-y-1">
        {/* ⚠ **A count, never the entries.** Each boundary is the verbatim
            text of a beat somebody pressed the card on, so a rendered list is
            a readable log of what upset whom — the one thing a veto that
            "never reveals who pressed it" must not accumulate in public. It
            used to go to one player's screen. The text goes to the DM and
            nowhere else; the *count* of recent presses goes to the room,
            because a count names nobody.

            The Lift stays because the card records the beat that was on
            screen, which is occasionally the wrong one — and it takes the
            newest, which is the case that happens. It sends `liftLast` rather
            than a filtered list, since a filtered list would mean this had
            read them. */}
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {X_CARD_BOUNDARY_HELP}
        </p>
        {boundaryCount === 0 ? (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Nothing raised.</p>
        ) : (
          <div className="flex items-center gap-2">
            <span className="flex-1 text-xs text-neutral-600 dark:text-neutral-400">
              {boundaryCount === 1 ? '1 boundary stands.' : `${boundaryCount} boundaries stand.`}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => save({ liftLast: true })}
              className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs disabled:opacity-40 dark:border-neutral-700"
            >
              Lift the most recent
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A text field that saves on demand rather than on every keystroke.
 *
 * Deliberately not debounced-autosave like the notes panel: this is read into
 * the system prompt, and a half-typed persona reaching the model mid-sentence
 * would change how the DM writes for however long the pause lasted.
 */
function PersonaField({
  id,
  label,
  hint,
  rows,
  value,
  disabled,
  onSave,
}: {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly rows: number;
  readonly value: string;
  readonly disabled: boolean;
  readonly onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  // Re-syncs when the server's answer differs from what is on screen — a save
  // elsewhere, or a trim the server applied.
  useEffect(() => setDraft(value), [value]);

  return (
    <div className="space-y-1">
      <label className="block text-xs text-neutral-500 dark:text-neutral-400" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || draft === value}
          onClick={() => onSave(draft)}
          className="rounded-md border border-neutral-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-neutral-700"
        >
          Save
        </button>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{hint}</span>
      </div>
    </div>
  );
}
